/* eslint-disable no-console */

import axios from 'axios';
import { getAccessToken } from '@neotech-solutions-org/neofusion-fe-shared';
import { getApiUrl } from './urlUtils';
import { getServiceHeaders } from './headers';
import { isRequestPublic } from './isRequestPublic';
import { ErrorObject } from '../@types/api';
import { runtimeEnv } from '../config/runtimeEnv';

export let casinoAccessToken: string | null = null;
const casinoAccessTokenListeners = new Set<() => void>();

export const getCasinoAccessToken = () => casinoAccessToken;

export const subscribeToCasinoAccessToken = (listener: () => void) => {
  casinoAccessTokenListeners.add(listener);

  return () => {
    casinoAccessTokenListeners.delete(listener);
  };
};

export const setCasinoAccessToken = (token: string | null) => {
  casinoAccessToken = token;
  casinoAccessTokenListeners.forEach((listener) => listener());
};

export const API_SERVICES = {
  FEED: { url: 'sportsbook-feed-api', name: 'feed' },
  WALLET: { url: 'sb-wallet-api', name: 'wallet' },
  CRM: { url: 'sportsbook-crm-api', name: 'crm' },
  CASINO: { url: 'casino-api', name: 'casino' },
} as const;

export type ApiServiceKey = keyof typeof API_SERVICES;
export type ApiService = (typeof API_SERVICES)[ApiServiceKey]['name'];

export const createHttpInstance = (serviceKey: ApiServiceKey) => {
  const service = API_SERVICES[serviceKey];

  const envUrlMap = {
    FEED: runtimeEnv.apiFeedUrl,
    WALLET: runtimeEnv.apiWalletUrl,
    CRM: runtimeEnv.apiCrmUrl,
    CASINO: runtimeEnv.apiCasinoUrl,
  };

  const instance = axios.create({
    baseURL: envUrlMap[serviceKey] ?? getApiUrl(service.url),
  });

  const serviceHeaders = getServiceHeaders[service.name];

  if (serviceHeaders) {
    instance.interceptors.request.use(async (config) => {
      const resolvedHeaders = await Promise.all(serviceHeaders.map((provider) => provider(config)));

      for (const header of resolvedHeaders) {
        for (const [key, value] of Object.entries(header)) {
          config.headers.set(key, value);
        }
      }

      return config;
    });
  }

  return instance;
};

const axiosInstances = {
  feed: createHttpInstance('FEED'),
  wallet: createHttpInstance('WALLET'),
  crm: createHttpInstance('CRM'),
  casino: createHttpInstance('CASINO'),
} as const;

export type ApiInstances = typeof axiosInstances;

const shouldExecuteRequest = async (path: string, apiService: ApiService) => {
  const isPublic = isRequestPublic(path);

  if (isPublic) {
    return true;
  }

  if (apiService === 'casino') {
    if (!casinoAccessToken) {
      console.warn('No casino access token available, skipping request');
      return false;
    }
  } else {
    const token = await getAccessToken();
    if (!token) {
      console.warn('No token available, skipping request for non-public method');
      return false;
    }
  }

  return true;
};

const executeRequest = async (
  method: 'get' | 'post' | 'patch' | 'delete',
  path: string,
  body: unknown | null,
  apiService: ApiService = 'feed',
  queryParams?: unknown,
  customHeaders?: Record<string, string>
) => {
  const shouldExecute = await shouldExecuteRequest(path, apiService);

  if (!shouldExecute) {
    return undefined;
  }

  const instance = axiosInstances[apiService];

  if (method === 'get') {
    const { data } = await instance.get(path, {
      params: queryParams,
      headers: customHeaders,
    });
    return data;
  } else if (method === 'delete') {
    const { data } = await instance.delete(path, {
      data: body,
      headers: customHeaders,
    });
    return data;
  } else {
    const { data } = await instance[method](path, body, {
      headers: customHeaders,
    });
    return data;
  }
};

export const getData = async (
  url: string,
  queryParams?: unknown,
  apiService: ApiService = 'feed',
  customHeaders?: Record<string, string>
) => {
  return executeRequest('get', url, null, apiService, queryParams, customHeaders);
};

export const postData = async (
  url: string,
  body: unknown,
  apiService: ApiService = 'feed',
  customHeaders?: Record<string, string>
) => {
  return executeRequest('post', url, body, apiService, undefined, customHeaders);
};

export const patchData = async (
  url: string,
  body: unknown,
  apiService: ApiService = 'feed',
  customHeaders?: Record<string, string>
) => {
  return executeRequest('patch', url, body, apiService, undefined, customHeaders);
};

export const deleteData = async (url: string, body: unknown | null, apiService: ApiService = 'feed') => {
  return executeRequest('delete', url, body, apiService);
};

export const hasApiMorePages = (count: number, page: number, limit: number) => {
  const pagesCount = Math.ceil(count / limit);
  return page < pagesCount;
};

export const fetchCasinoAccessToken = async () => {
  try {
    const token = await getAccessToken();
    const response = await postData('/auth/authenticate', { token }, 'casino');
    return response?.accessToken;
  } catch (error) {
    console.error(error);
  }
};

export const ensureCasinoAccessToken = async () => {
  if (casinoAccessToken) {
    return casinoAccessToken;
  }

  const token = await fetchCasinoAccessToken();
  setCasinoAccessToken(token ?? null);
  return token ?? null;
};

export const sendEmailVerification = async (email?: string) => {
  try {
    const response = await getData('/email/resend', { email }, 'crm');
    return response;
  } catch (error) {
    console.error('Error resending email verification:', error);
  }
};

export const isFeedApiError = <U = Record<string, unknown>>(error: unknown): error is ErrorObject<U> => {
  return typeof error === 'object' && error !== null && 'code' in error && 'message' in error;
};
